import type {
  ApplicationInternalAccessSchema,
  InternalAccessCheckSchema,
  TestApplicationInternalAccessResponseSchema,
  UpsertApplicationInternalAccessBodySchema,
} from '@terra/shared-contracts-applications';
import type { z } from 'zod';

export type ApplicationInternalAccess = z.infer<
  typeof ApplicationInternalAccessSchema
>;
export type InternalAccessBindingPayload = z.infer<
  typeof UpsertApplicationInternalAccessBodySchema
>;
export type InternalAccessTestResult = z.infer<
  typeof TestApplicationInternalAccessResponseSchema
>;
type InternalAccessCheck = z.infer<typeof InternalAccessCheckSchema>;

export type InternalAccessCheckStatus = InternalAccessCheck['status'];

export const INTERNAL_NETWORK_TRANSPORTS = {
  TWINGATE: 'twingate',
  INTERNAL_ACCESS: 'internal-access',
} as const;

export type InternalNetworkTransport =
  (typeof INTERNAL_NETWORK_TRANSPORTS)[keyof typeof INTERNAL_NETWORK_TRANSPORTS];

export type InternalNetworkSummary = {
  id: string;
  displayName?: string | null;
};

export type InternalAccessNetworkSummary = InternalNetworkSummary & {
  connectorStatus: string;
};

export type ConnectorScanNetworkSummary = InternalNetworkSummary & {
  isActive: boolean;
  resourceProxy?: { url?: string | null } | null;
};

export type InternalConnectorSummary = {
  state: string;
};

export type InternalNetworkOption = {
  value: string;
  label: string;
  transport: InternalNetworkTransport;
  id: string;
};

export type InternalNetworkLabels = {
  network: string;
  internalAccess: string;
  resourceNetworks: string;
  legacy: string;
};

export type InternalNetworkTransportFlags = {
  isInternalAccessEnabled: boolean;
  isTwingateEnabled: boolean;
};

const TRANSPORT_PREFIXES = [
  INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS,
  INTERNAL_NETWORK_TRANSPORTS.TWINGATE,
] as const;

export const encodeInternalNetworkValue = ({
  transport,
  id,
}: {
  transport: InternalNetworkTransport;
  id: string;
}): string => `${transport}:${id}`;

export const parseInternalNetworkValue = (
  value: string,
): { transport: InternalNetworkTransport; id: string } | null => {
  const transport = TRANSPORT_PREFIXES.find((prefix) =>
    value.startsWith(`${prefix}:`),
  );

  if (transport) {
    return { transport, id: value.slice(transport.length + 1) };
  }

  return value
    ? { transport: INTERNAL_NETWORK_TRANSPORTS.TWINGATE, id: value }
    : null;
};

export const formatInternalNetworkOptionLabel = ({
  name,
  transport,
  includeTransport,
  labels,
}: {
  name: string;
  transport: InternalNetworkTransport;
  includeTransport: boolean;
  labels: InternalNetworkLabels;
}): string => {
  if (!includeTransport) {
    return name;
  }

  if (transport === INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS) {
    return `${name} · ${labels.internalAccess}`;
  }

  return `${name} · ${labels.resourceNetworks} (${labels.legacy})`;
};

const toNetworkOptions = ({
  networks,
  transport,
  includeTransport,
  labels,
}: {
  networks: readonly InternalNetworkSummary[];
  transport: InternalNetworkTransport;
  includeTransport: boolean;
  labels: InternalNetworkLabels;
}): InternalNetworkOption[] =>
  networks.map((network, index) => ({
    value: encodeInternalNetworkValue({ transport, id: network.id }),
    label: formatInternalNetworkOptionLabel({
      name: network.displayName || `${labels.network} ${index + 1}`,
      transport,
      includeTransport,
      labels,
    }),
    transport,
    id: network.id,
  }));

export const buildInternalNetworkOptions = ({
  internalAccessNetworks,
  twingateNetworks,
  flags,
  labels,
}: {
  internalAccessNetworks: readonly InternalNetworkSummary[];
  twingateNetworks: readonly InternalNetworkSummary[];
  flags: InternalNetworkTransportFlags;
  labels: InternalNetworkLabels;
}): InternalNetworkOption[] => {
  const includeTransport =
    flags.isInternalAccessEnabled &&
    flags.isTwingateEnabled &&
    internalAccessNetworks.length > 0 &&
    twingateNetworks.length > 0;

  return [
    ...(flags.isInternalAccessEnabled
      ? toNetworkOptions({
          networks: internalAccessNetworks,
          transport: INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS,
          includeTransport,
          labels,
        })
      : []),
    ...(flags.isTwingateEnabled
      ? toNetworkOptions({
          networks: twingateNetworks,
          transport: INTERNAL_NETWORK_TRANSPORTS.TWINGATE,
          includeTransport,
          labels,
        })
      : []),
  ];
};

export const isConnectorScanNetworkEligible = (
  network: Pick<ConnectorScanNetworkSummary, 'isActive' | 'resourceProxy'>,
): boolean => network.isActive && !network.resourceProxy?.url;

export const buildConnectorScanNetworkOptions = ({
  internalAccessNetworks,
  selectedNetworkId,
  isInternalAccessEnabled,
  networkFallbackLabel,
}: {
  internalAccessNetworks: readonly ConnectorScanNetworkSummary[];
  selectedNetworkId?: string | null;
  isInternalAccessEnabled: boolean;
  networkFallbackLabel: string;
}): InternalNetworkOption[] => {
  if (!isInternalAccessEnabled) {
    return [];
  }

  return toNetworkOptions({
    networks: internalAccessNetworks.filter(
      (network) =>
        network.id === selectedNetworkId ||
        isConnectorScanNetworkEligible(network),
    ),
    transport: INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS,
    includeTransport: false,
    labels: {
      network: networkFallbackLabel,
      internalAccess: '',
      resourceNetworks: '',
      legacy: '',
    },
  });
};

export const resolvePersistedInternalNetworkValue = ({
  internalAccess,
  flags,
}: {
  internalAccess: ApplicationInternalAccess | null;
  flags: InternalNetworkTransportFlags;
}): string => {
  if (!internalAccess?.isBindingPersisted) {
    return '';
  }

  if (flags.isInternalAccessEnabled && internalAccess.internalAccessNetworkId) {
    return encodeInternalNetworkValue({
      transport: INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS,
      id: internalAccess.internalAccessNetworkId,
    });
  }

  if (flags.isTwingateEnabled && internalAccess.twingateNetworkId) {
    return encodeInternalNetworkValue({
      transport: INTERNAL_NETWORK_TRANSPORTS.TWINGATE,
      id: internalAccess.twingateNetworkId,
    });
  }

  return '';
};

export const findConfiguredInternalNetworkLabel = ({
  internalAccess,
  options,
}: {
  internalAccess: ApplicationInternalAccess | null;
  options: readonly InternalNetworkOption[];
}): string | null => {
  if (!internalAccess) {
    return null;
  }

  const configured = options.find(
    (option) =>
      (option.transport === INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS &&
        option.id === internalAccess.internalAccessNetworkId) ||
      (option.transport === INTERNAL_NETWORK_TRANSPORTS.TWINGATE &&
        option.id === internalAccess.twingateNetworkId),
  );

  return configured?.label ?? null;
};

export const isInternalAccessBindingDirty = ({
  selectedValue,
  persistedValue,
  internalAccess,
  isPendingEnable,
}: {
  selectedValue: string;
  persistedValue: string;
  internalAccess: ApplicationInternalAccess | null;
  isPendingEnable: boolean;
}): boolean =>
  isPendingEnable ||
  (Boolean(selectedValue) && selectedValue !== persistedValue) ||
  (Boolean(selectedValue) && internalAccess?.isBindingPersisted === false);

export const buildInternalAccessBindingPayload = ({
  selectedValue,
  flags,
}: {
  selectedValue: string;
  flags: InternalNetworkTransportFlags;
}): InternalAccessBindingPayload | null => {
  const parsed = parseInternalNetworkValue(selectedValue);

  if (!parsed) {
    return null;
  }

  if (parsed.transport === INTERNAL_NETWORK_TRANSPORTS.INTERNAL_ACCESS) {
    return flags.isInternalAccessEnabled
      ? {
          internalAccessNetworkId: parsed.id,
          ...(flags.isTwingateEnabled ? { twingateNetworkId: null } : {}),
        }
      : null;
  }

  return flags.isTwingateEnabled
    ? { twingateNetworkId: parsed.id, internalAccessNetworkId: null }
    : null;
};

const ALIVE_CONNECTOR_STATES: ReadonlySet<string> = new Set([
  'ALIVE',
  'ACTIVE',
  'ONLINE',
]);

export const hasInternalConnectors = ({
  connectors,
  internalAccessNetworks,
}: {
  connectors: readonly InternalConnectorSummary[];
  internalAccessNetworks: readonly InternalAccessNetworkSummary[];
}): boolean =>
  connectors.length > 0 ||
  internalAccessNetworks.some(
    (network) => network.connectorStatus !== 'REVOKED',
  );

export const hasAliveInternalConnector = ({
  connectors,
  internalAccessNetworks,
}: {
  connectors: readonly InternalConnectorSummary[];
  internalAccessNetworks: readonly InternalAccessNetworkSummary[];
}): boolean =>
  connectors.some((connector) => ALIVE_CONNECTOR_STATES.has(connector.state)) ||
  internalAccessNetworks.some(
    (network) => network.connectorStatus === 'ACTIVE',
  );

const INFRA_CHECK_CODES: ReadonlySet<InternalAccessCheck['code']> = new Set([
  'PROXY_DEPLOYMENT',
  'PROXY_HEALTH',
  'EGRESS_POD',
  'EGRESS_HEALTH',
]);

export const INTERNAL_ACCESS_CHECK_LABEL_KEYS = {
  NETWORK_CONFIGURED: 'networkConfigured',
  RESOURCE_RESOLVABLE: 'resourceResolvable',
  ROUTE_RESOLVABLE: 'resourceResolvable',
  CONNECTOR_ALIVE: 'connectorAlive',
  EGRESS_PROBE: 'egressProbe',
  PROXY_SERVICE: 'proxyService',
} as const;

const USER_CHECK_CODES = [
  'NETWORK_CONFIGURED',
  'RESOURCE_RESOLVABLE',
  'ROUTE_RESOLVABLE',
  'CONNECTOR_ALIVE',
  'EGRESS_PROBE',
] as const;

type UserCheckCode = (typeof USER_CHECK_CODES)[number];

export type InternalAccessDisplayCheck = {
  id: string;
  labelKey: (typeof INTERNAL_ACCESS_CHECK_LABEL_KEYS)[keyof typeof INTERNAL_ACCESS_CHECK_LABEL_KEYS];
  detail: string;
  status: InternalAccessCheckStatus;
};

const isUserCheckCode = (
  code: InternalAccessCheck['code'],
): code is UserCheckCode =>
  USER_CHECK_CODES.some((userCode) => userCode === code);

export const buildInternalAccessDisplayChecks = (
  checks: readonly InternalAccessCheck[],
): InternalAccessDisplayCheck[] => {
  const infraFailure = checks.find(
    (check) => INFRA_CHECK_CODES.has(check.code) && check.status === 'fail',
  );
  const proxyServiceCheck: InternalAccessDisplayCheck[] = infraFailure
    ? [
        {
          id: 'PROXY_SERVICE',
          labelKey: INTERNAL_ACCESS_CHECK_LABEL_KEYS.PROXY_SERVICE,
          detail: infraFailure.message,
          status: 'fail',
        },
      ]
    : [];
  const userChecks = checks.flatMap((check): InternalAccessDisplayCheck[] => {
    const { code } = check;

    if (!isUserCheckCode(code) || check.status === 'skip') {
      return [];
    }

    return [
      {
        id: code,
        labelKey: INTERNAL_ACCESS_CHECK_LABEL_KEYS[code],
        detail: code === 'NETWORK_CONFIGURED' ? '' : check.message,
        status: check.status,
      },
    ];
  });

  return [...proxyServiceCheck, ...userChecks];
};
